/**
 * DraftVoyageBanner.js
 *
 * FT-269: sticky banner shown at the top of the Voyages module when a
 * DRAFT voyage is the active record for the vessel. Offers "Resume"
 * (opens StartVoyageFullScreen pre-filled) and "Discard" (calls
 * abortDraftVoyage with reason 'user_discarded' after a confirm).
 *
 * Hidden when there's no DRAFT — the consumer is responsible for not
 * mounting it in that case OR passing `draftVoyage={null}`.
 */

import React from 'react';
import { Clock, RefreshCcw, Trash2 } from 'lucide-react';

function formatRelative(iso) {
  if (!iso) return 'just now';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'just now';
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.round(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.round(diff / 3_600_000)} h ago`;
  return `${Math.round(diff / 86_400_000)} d ago`;
}

const DraftVoyageBanner = ({ draftVoyage, onResume, onDiscard }) => {
  if (!draftVoyage) return null;

  const handleDiscard = () => {
    const confirmed = typeof window !== 'undefined'
      ? window.confirm('Discard this voyage draft? This cannot be undone — the draft will appear under Archived/Aborted.')
      : true;
    if (confirmed && typeof onDiscard === 'function') {
      onDiscard(draftVoyage.id);
    }
  };

  return (
    <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 sm:px-6 flex items-center justify-between gap-3" role="status">
      <div className="flex items-center gap-3 min-w-0">
        <Clock className="h-5 w-5 text-amber-600 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-sm font-medium text-amber-900 truncate">
            Voyage draft in progress
            {draftVoyage.voyageNumber ? ` — ${draftVoyage.voyageNumber}` : ''}
          </p>
          <p className="text-xs text-amber-800">
            last updated {formatRelative(draftVoyage.lastDraftUpdatedAt || draftVoyage.draftStartedAt)}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={() => typeof onResume === 'function' && onResume(draftVoyage.id)}
          className="inline-flex items-center gap-1.5 min-h-[40px] px-3 py-2 bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium rounded-md transition-colors"
        >
          <RefreshCcw className="h-4 w-4" aria-hidden="true" />
          Resume
        </button>
        <button
          type="button"
          onClick={handleDiscard}
          className="inline-flex items-center gap-1.5 min-h-[40px] px-3 py-2 bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 text-sm font-medium rounded-md transition-colors"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          Discard
        </button>
      </div>
    </div>
  );
};

export default DraftVoyageBanner;
